export interface SafetyBehavior {
  behaviorType: string;
  count: number;
  scoreImpact: number;
}

export interface SafetySpeeding {
  speedingType: string;
  durationSeconds: number;
  scoreImpact: number;
}

export interface SafetyReport {
  available: boolean;
  reason?: string;
  driverId: string;
  punchCardId: string;
  windowStart: string; // ISO
  windowEnd: string; // ISO
  windowEndBasis: string;
  vehicleScore?: number;
  driveDistanceMiles?: number;
  driveTimeMinutes?: number;
  behaviors: SafetyBehavior[];
  speeding: SafetySpeeding[];
  fetchedAt?: string;
}

export interface SafetySummary {
  weightedScore?: number; // undefined when the endpoint returns null
  reportCount: number;
  totalDriveMinutes: number;
  totalDriveMiles: number;
}

export interface SafetyPeriodView {
  id: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  previousId?: string;
  nextId?: string;
}

export interface DriverSafetySummary {
  payPeriod: SafetyPeriodView;
  summary: SafetySummary;
  reports: SafetyReport[];
}

const REASON_LABELS: Record<string, string> = {
  no_schedule: "Driver had no schedule that day",
  no_vehicle_assigned: "No vehicle assigned for this shift",
  vehicle_not_linked: "Vehicle isn't linked to Samsara",
  pending: "Report still processing",
  failed: "Report failed to generate",
};

export const safetyReasonLabel = (reason: string): string =>
  REASON_LABELS[reason] || "Report unavailable";

const BEHAVIOR_LABELS: Record<string, string> = {
  harshAccel: "Harsh acceleration",
  harshBrake: "Harsh braking",
  harshTurn: "Harsh turn",
  crash: "Crash",
  speedingManual: "Speeding (manual)",
  mobileUsage: "Mobile usage",
  inattentiveDriving: "Inattentive driving",
  followingDistance: "Following too close",
  rollingStop: "Rolling stop",
  didNotYield: "Did not yield",
  policyViolation: "Policy violation",
};

// De-camelCase an unknown type: "someNewThing" -> "Some new thing".
const humanizeCamel = (s: string): string => {
  const spaced = s.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const behaviorTypeLabel = (behaviorType: string): string =>
  BEHAVIOR_LABELS[behaviorType] || humanizeCamel(behaviorType);

const SPEEDING_LABELS: Record<string, string> = {
  light: "Light speeding",
  moderate: "Moderate speeding",
  heavy: "Heavy speeding",
  severe: "Severe speeding",
};

export const speedingTypeLabel = (speedingType: string): string =>
  SPEEDING_LABELS[speedingType] || `${humanizeCamel(speedingType)} speeding`;

const BASIS_LABELS: Record<string, string> = {
  check_out: "ended at check-out",
  last_activity: "ended at last activity",
  eod_cutoff: "ended at end-of-day cutoff",
};

export const windowBasisLabel = (basis: string): string =>
  BASIS_LABELS[basis] || "";
